import { useRef, useState, type DragEvent, type ReactNode } from 'react'
import { useAssetLibrary } from '@/context/AssetLibraryContext'
import { useToast } from '@/context/ToastContext'
import { Icon, type IconName } from './icons'
import { ImageLightbox } from './ImageLightbox'
import { SmartImage, isVideoUrl } from './SmartImage'
import { Badge, Button, Card, CardBody, CardHead, ClearButton, Field } from './ui'

/**
 * 参考图 / 参考视频上传器 —— 全站唯一实现。
 *
 * 为什么必须封装：这套「拖拽上传 + 缩略图 + 从素材库选择」在 6 个生成页里
 * 各写了一遍（Banner / 套图 / 主图详情 / 专业服饰 / 图片工具箱 / 视频）。
 * 后果就是本次反馈的这类问题 —— 某个页面漏了「素材库按钮」、
 * 某个页面缩略图没接 onRemove、某个页面的上限写死成 4 张而另一个写 6 张，
 * 修的时候还得挨个页面找。**同一份控件在两个文件里各写一遍就是 bug**（项目铁律）。
 *
 * 自带能力：
 *   · 拖拽上传（多选） / 点击上传
 *   · 缩略图**拖拽排序**（HTML5 DnD，零依赖）—— 第一张就是要传给上游的主图，顺序有语义
 *   · 缩略图**点击预览**（图片放大 / 视频直接播）
 *   · 单张删除、**一键清空**（`ClearButton`，带内联二次确认）、张数上限、超限提示
 *   · 「从素材库选择」多选回填（走 AssetLibraryContext 的 pickMany）
 *   · 图床加载失败自动退本地副本（复用 assetFallback 的映射，实现只有 SmartImage 一份）
 *
 * ---------------------------------------------------------------------------
 * 缩略图只许有一种渲染方式：交给 `SmartImage`（2026-10-08）
 * ---------------------------------------------------------------------------
 * 用户原话：「为什么上传后是这样的，应该可以预览啊」（截图：参考视频传上去了，
 * 缩略图是一个碎图标）。
 *
 * 两个独立缺陷叠在一起：
 *   1. **视频被当图片画。** 这一格早先写的是 `<img src={u}>`，而 `<img>` 不解码视频
 *      —— `<img src="x.mp4">` 是**必然失败**的，缩略图永远落在碎图标上。
 *      `SmartImage` 的注释里早就写着这条（素材库 / 结果网格都踩过），
 *      唯独本组件漏了：它自己另写了一份 `<img>` + onError 降级状态机。
 *   2. **已选缩略图根本没有预览入口。** 候选格有放大键，真正要喂给上游的那几张
 *      反而点不动 —— 76px 的格子里看不出传没传错文件。
 *
 * 规矩：**元素与降级链都只有 `SmartImage` 一份**，本组件只提供两样东西 ——
 * 占位长什么样（`renderFallback` 交回自己的 `.refup__broken`，验收脚本按它判裂图）
 * 与点击后开哪个遮罩（`ImageLightbox`，视频会换成带 controls 的播放器）。
 * ⚠️ 别再往这里加回一份 `<img>`：加回来的那天，视频缩略图就又坏了。
 *
 * 两个坑：
 *   1. 缩略图网格**不能**放进 `<label>` 内部 —— label 会把点击转发给文件选择框，
 *      于是「点缩略图想拖排序」变成了弹系统文件框。
 *   2. 网格里的 drop 必须 `stopPropagation`，否则拖拽排序的松手会冒泡到上传区，
 *      被当成一次文件投放。
 *
 * ---------------------------------------------------------------------------
 * `wrap="cell"` —— 表格单元格里的第三种形态（2026-09-30）
 * ---------------------------------------------------------------------------
 * 批量出图工作台（`/batch`）的清单是**一行一个产品、每行各自一份参考图**。
 * 用户口径：「参考图这块……可以提供图片上传」（指的是表格第 8 列那个格子）。
 *
 * 为什么不给那一页另写一个「小号上传器」：
 *   「参考图上传只许一份实现」是本项目的头号铁律，而 `smoke-nav` 有一条**硬断言**
 *   ——任何 tsx 里出现 `accept` 含 image 的文件框、且不是本文件，直接判失败。
 *   给表格开一个例外，就等于把「某页漏了清空 / 上限写死成 4 / 缩略图没接删除」
 *   这几种历史事故重新放回门里。所以是**本组件多一种排版**，不是多一个控件。
 *
 * cell 形态与 card 形态的差别**只有排版**，能力和行为一个字都没变：
 *   · 拖拽区（106px 的大方块）换成一个小号「＋ 上传」按钮，但**投放目标变成整个
 *     单元格**（拖文件到格子里任何位置都算）—— 格子本来就小，再画一个大方块没有意义；
 *   · 缩略图 76px → 34px、去掉角标数字（34px 的图上挂两位数读不清）；
 *   · 素材库 / 清空按钮收成图标（`ClearButton` 的 `iconOnly`），否则 420px 的表列
 *     里一行放不下。
 * 上限、去重、「第一张是主图」的边框提示、裂图退本地副本、上传走
 * `AssetLibraryContext` —— 全部沿用下面那一份逻辑，没有第二条路径。
 *
 * ---------------------------------------------------------------------------
 * 转圈必须是**本实例**的（2026-09-30）
 * ---------------------------------------------------------------------------
 * 用户口径（圈着批量清单第 2、3 行的转圈图标）：「这个上传应该单独 row 去 loading」。
 *
 * 症状是**一传全转**：`/batch` 的清单里只有一个格子真的在传文件，所有格子的
 * 「＋ 上传」却一起变成了转圈 —— 因为指示器读的是 context 里那个**全局** `uploading`
 * （`AssetLibraryContext` 的计数器，只要有任意一处在上传就为真）。
 * 于是「第 3 行在传」看起来像「每一行都在传」，用户没法判断该等哪一行，
 * 而且 20 行的表里有 20 个转圈，视觉上像整页卡住了。
 *
 * 规据只有一条：**上传指示只读本实例的 `busy`**（`handleFiles` 自己 set/清）。
 * 全局的 `uploading` 留给「素材库抽屉」——那里确实是在说「这个库正在进文件」，
 * 全局语义是对的；但一个**每行一个**的控件拿全局标志去点亮自己，就是错的。
 * `smoke-nav` 有一条反向断言钉着这件事（`RefUploader` 里不许再出现 `uploading`）。
 */
export function RefUploader({
  urls,
  onChange,
  wrap = 'card',
  title = '参考图',
  hint,
  icon = 'image',
  label,
  required,
  fieldHint,
  max = 6,
  kind = 'image',
  dropLabel,
  dropHint,
  firstBadge = '主图',
  source,
  footnote,
  emptyHint,
}: {
  urls: string[]
  onChange: (urls: string[]) => void
  /**
   * card：自带卡片外壳；none：只出控件，由页面自己套壳；
   * cell：**塞进表格单元格的紧凑形态**（见下方注释）。
   */
  wrap?: 'card' | 'none' | 'cell'
  title?: string
  hint?: string
  icon?: IconName
  label?: string
  required?: boolean
  fieldHint?: string
  max?: number
  kind?: 'image' | 'video' | 'all'
  dropLabel?: string
  dropHint?: string
  /**
   * 第一张缩略图上的角标文案，默认「主图」。
   *
   * 给**产品图**用 —— 第一张就是要还原的主视角，顺序有语义。
   * 但有些通道天然没有主副之分（视频页的「场景参考图」：它只提供氛围，
   * 不参与产品还原，且一般只传一张），挂着「主图」会让人以为还有一组副图。
   * 那类通道传空串即可关掉角标。
   */
  firstBadge?: string
  /**
   * 参考图的**另一条来源**：从别处已有工作流的产出里挑，而不是上传。
   *
   * ⚠️ 默认（不传 `withUpload`）它是**独占**的：上传区和「从素材库选择」
   * 都不渲染 —— 这一档的参考图只可能来自那里（典型是「场景图」）。
   * 这条口径用户说过两次，是同一条：
   *   2026-09-29「这商品图是AI模特图，场景图不需要上传商品组件」
   *   2026-09-30「商品什么鬼去掉啊，这个是场景图」
   *
   * 传 `withUpload: true` 则是「两条路并存」：候选条列出去哪里挑，
   * 同时候选条下面照旧能拖拽上传 / 从素材库选。
   * 2026-10-06 用户口径：「`/digital-human` 直接引用 ai 模特或者上传人像才对」——
   * 形象图这件事上「引用 AI 模特」与「自己拍一张」是**同一张脸的两种取法**，
   * 只给一条路会逼用户先绕去别的页面。**哪一种，由调用方按档位说清楚，
   * 组件不猜**（猜错的表现是上传区静默消失，截图上看不出来）。
   *
   * 为什么候选条长在本组件里、而不是各页再写一个「产出选择器」：
   * 缩略图 / 删除 / 清空 / 张数上限 / 裂图回退到本地副本，这五件事与上传通道
   * **完全同构**。新写一个选择器就是把它们再抄一遍 —— 抄漏哪一件，
   * 截图上都看不出来（按钮没渲染 ≠ 报错），这正是本项目头号 bug 类型。
   */
  source?: {
    /** 来源工作流名，用于文案（如「AI 模特图」） */
    name: string
    /**
     * 候选条标题。默认「{name}的产出」—— 来源是**某一档**时这么写正好。
     * 哪天来源放宽成「本页所有档」，就得换一句人话，
     * 否则会念成「本页产出的产出」。与 `firstBadge` 同理：**只改文案，不改能力**。
     * （原本给「场景图」留的开关；场景图 2026-09-30 起整块不要参考图了，
     * 2026-10-06 数字人这一档重新用上了「跨两档」的写法。）
     */
    title?: string
    /** 候选为空时的说明。默认「本页还没跑过「{name}」—— …」*/
    emptyText?: string
    /** 空态那个按钮的文案。默认「去生成{name}」*/
    goLabel?: string
    /** 候选图地址，调用方按**新 → 旧**排好（第一张会被页面用作默认选中） */
    candidates: string[]
    /**
     * 后台**正在生成几张** —— 在候选网格最前面占这么多个「生成中」格子。
     *
     * 为什么要有：这是一条异步链路（提交后立即返回、出图在后台），但候选条
     * 在出图之前**什么都没有**，于是「后台在跑」这件事在界面上没有任何位置，
     * 用户只看得见按钮上一个转圈 —— 读起来像「卡住了」。
     * 用户的原话就是「**分镜生成为什么不是异步的**」：它确实是异步的，问题是看不出来。
     *
     * ⚠️ 占位格用的是 `refup__pending`，**不是** `refup__pick-item` ——
     * 后者是「可点的候选图」的计数口径（验收脚本按它数候选），
     * 混进去会让「候选有几张」这个读数凭空多出来。
     */
    pending?: number
    /** 候选为空时把人送过去（跳去那一档） */
    onGo?: () => void
    /**
     * 候选条之外**同时保留上传通道**（拖拽 / 素材库）。
     * 见上方那段：只在「同一种输入物的两种取法」时才该打开，
     * 是为了「随便传一张也能替代」的档位别开它。
     */
    withUpload?: boolean
  }
  footnote?: ReactNode
  emptyHint?: ReactNode
}) {
  const { upload, pickMany } = useAssetLibrary()
  const toast = useToast()
  const [over, setOver] = useState(false)
  const [busy, setBusy] = useState(false)
  const dragFrom = useRef<number | null>(null)
  const [overIdx, setOverIdx] = useState<number | null>(null)
  /**
   * 放大预览的目标（`null` = 没打开）—— **两份清单共用一个遮罩**。
   *
   * `picked` = 本控件里已选的那几张（上传 / 素材库来的）；
   * `candidates` = `source` 候选条里那几张。
   *
   * ⚠️ **必须带上 `list` 一起存**：两份清单的下标毫无关系，
   * 只存一个数字的话，「先点候选图放大、关掉、再点已选缩略图」会拿候选的下标
   * 去索引已选数组 —— 放大出来的是**另一张图**，而且两张都真实存在，
   * 用户只会觉得「这功能点错了」。
   *
   * 为什么要放大：缩略图只有 76px，而候选那张是要拿去出片的 ——
   * 脸是不是歪的、手里到底拿没拿产品、手指有没有崩，在这个尺寸上根本看不出来。
   * 用户口径：「为什么不能放大看的」。
   *
   * ⚠️ 候选格里「放大」与「选这张」是**两个动作**，不许合成一个（点图 = 放大，
   * 那就没法选中了；点图 = 选中，那就没法放大）。所以候选格的放大键是它的
   * **兄弟节点**：`<button>` 套 `<button>` 是非法 HTML，浏览器会把内层丢出去。
   * 已选缩略图没有「选中」这个语义（点它只是看），所以整格可点，不加第二颗按钮。
   */
  const [zoom, setZoom] = useState<{ list: 'picked' | 'candidates'; at: number } | null>(null)

  const noun = kind === 'video' ? '视频' : kind === 'image' ? '图片' : '文件'
  const full = urls.length >= max
  const room = Math.max(0, max - urls.length)
  const isCell = wrap === 'cell'

  const accept = kind === 'video' ? 'video/*' : kind === 'image' ? 'image/*' : 'image/*,video/*'

  /** 统一入口：去重 + 截断到上限。上传和素材库回填都走这里，避免两处各写一遍上限逻辑 */
  const merge = (incoming: string[]) => {
    const next = [...urls]
    incoming.forEach((u) => {
      if (u && !next.includes(u) && next.length < max) next.push(u)
    })
    onChange(next)
    return next.length - urls.length
  }

  const handleFiles = async (files: FileList | File[] | null) => {
    const list = files ? Array.from(files) : []
    if (!list.length) return
    if (full) {
      toast.error(`最多 ${max} 个${noun}`)
      return
    }
    if (list.length > room) toast.error(`上限 ${max} 个，本次只收前 ${room} 个`)
    setBusy(true)
    try {
      const added = await upload(list.slice(0, room))
      merge(added.map((a) => a.url))
    } finally {
      setBusy(false)
    }
  }

  const fromLibrary = async () => {
    if (full) {
      toast.error(`最多 ${max} 个${noun}，先移除一些再选`)
      return
    }
    const picked = await pickMany(room)
    if (!picked.length) return
    const added = merge(picked.map((a) => a.url))
    if (added > 0) toast.success(`已加入 ${added} 个`)
  }

  const move = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= urls.length || to >= urls.length) return
    const next = [...urls]
    const [item] = next.splice(from, 1)
    next.splice(to, 0, item)
    onChange(next)
  }

  const removeAt = (i: number) => onChange(urls.filter((_, idx) => idx !== i))

  /**
   * 裂图占位 —— 交给 `SmartImage` 的 `renderFallback` 画。
   *
   * 这里**不再自己维护一份「主地址 → 本地副本 → 认裂」的状态机**：
   * 那套降级链 `SmartImage` 已经有一份（素材库 / 结果网格 / 任务卡共用），
   * 本组件早先另写了一遍，于是视频缩略图被漏在外面 —— 它走 `<img src=x.mp4>`，
   * 而 `<img>` 不解码视频，**必然失败**，缩略图永远是碎图标（本次用户报障）。
   * 现在元素与降级链都只有一份，`SmartImage` 认视频、这里只负责占位长什么样。
   */
  const brokenBox = (
    <span className="refup__broken" title="加载失败">
      <Icon name="alert" size={14} />
    </span>
  )
  const renderFallback = () => brokenBox

  /**
   * 放大列表 —— 按 `zoom.list` 指向哪份清单现算。
   *
   * `type` 必须逐项判（`kind` 属性管不了这里：候选条与已选列表都可能是
   * `all`，同一条候选条里图片视频混着）。判错的后果是视频被当图片塞进
   * 灯箱的 `<img>` —— 又一次「看着像图挂了」。
   */
  const zoomItems = (zoom
    ? zoom.list === 'picked'
      ? urls
      : (source?.candidates ?? [])
    : []
  ).map((u) => ({ url: u, type: isVideoUrl(u) ? ('video' as const) : ('image' as const) }))

  /**
   * 候选条点击 —— 语义是「用这张 / 不用这张」。
   *
   * 满了还点一张**没选过**的，说明用户想换，而不是想加（`max` 为 1 的档位
   * 就只有这一种可能）：直接顶掉最早那张。若这里改成 toast 报「已满」，
   * 用户就得先点一次 × 再点一次新图 —— 两步做一件「换一张」的事。
   */
  const toggleCandidate = (u: string) => {
    if (urls.includes(u)) {
      onChange(urls.filter((x) => x !== u))
      return
    }
    onChange(full ? [...urls.slice(1), u] : [...urls, u])
  }

  /**
   * 投放行为 —— card / cell 两个形态共用同一份。
   * cell 形态把它挂在**整个格子上**，card 形态挂在那个大方块上；
   * 差异只有「谁接这个事件」，`handleFiles` 还是那一个。
   */
  const dropProps = {
    onDragOver: (e: DragEvent) => {
      e.preventDefault()
      /*
       * cell 形态：**这一格的投放由这一格收，别再往外冒**。
       *
       * 外面那层是 /batch 的「拖到清单上按文件名归位」外壳（`BatchImageDrop`）。
       * 不断开的话，往「第 3 行」的参考图格上丢一张图会同时触发两件事：
       * ① 这张图归到第 3 行；② 再按文件名猜一次、猜中哪行就归哪行 ——
       * 同一张图被上传两遍、可能落在两行上，而且**两边都报成功**。
       * 高亮同理：两个框一起亮，用户分不清是落在行上还是落在表上。
       */
      if (isCell) e.stopPropagation()
      if (!full) setOver(true)
    },
    onDragLeave: (e: DragEvent) => {
      if (isCell) e.stopPropagation()
      setOver(false)
    },
    onDrop: (e: DragEvent) => {
      e.preventDefault()
      if (isCell) e.stopPropagation()
      setOver(false)
      void handleFiles(e.dataTransfer.files)
    },
  }

  /**
   * 文件选择框 —— 只此一个定义，两个形态各把它挂进自己的外壳里。
   * 「点击哪个东西会弹系统文件框」是唯一会变的部分：大方块 / 一个小号「＋」。
   */
  const fileInput = (
    <input
      type="file"
      accept={accept}
      multiple
      style={{ display: 'none' }}
      onChange={(e) => {
        void handleFiles(e.target.files)
        e.target.value = ''
      }}
    />
  )

  /*
   * 计数（`n/6`）—— cell 形态把它挪到缩略图那一行的末尾。
   *
   * 因为格子只有 ~140px 宽：`上传` + `素材库` + `计数` + `清空` 四个东西并排放不下，
   * 一定会折成两行，而行高会从 88px 涨到 115px（实测）。
   * 计数本来就不需要挨着按钮（它是「选了几张」的读数，贴在图上更近）。
   * 没有图时格子不渲染网格，那就还留在工具条里（否则 `0/6` 无处安放）。
   */
  const countNode = (
    <span className="refup__count">
      {isCell ? `${urls.length}/${max}` : `${urls.length}/${max} ${noun}`}
    </span>
  )
  const countInBar = !isCell || urls.length === 0

  /*
   * cell 形态：**整个格子都是落点**。格子里再画一个 106px 的虚线方块
   * 既放不下也没有意义（用户拖图时瞄的是「那一行的参考图格」）。
   *
   * ⚠️ 这里**不能**和下面那个 `<label>` 同时接 drop：同一件事会跑两遍，
   * 表现是「同一张图被传了两次」。
   */
  const control = (
    <div
      className={`refup${isCell ? ' refup--cell' : ''}${isCell && over ? ' is-over' : ''}`}
      {...(isCell ? dropProps : {})}
    >
      {source ? (
        <div className="refup__pick">
          <div className="refup__pick-head">
            <span className="refup__pick-title">
              {source.title ?? `${source.name}的产出`}
            </span>
            {source.pending ? (
              <span className="refup__pick-sub refup__pick-sub--busy">
                <span className="spinner" /> 正在生成 {source.pending} 张…
              </span>
            ) : source.candidates.length ? (
              <span className="refup__pick-sub">
                {source.candidates.length} 张 · 点一张设为参考图
              </span>
            ) : null}
          </div>
          {source.candidates.length || source.pending ? (
            <div className="refup__pick-row">
              {/*
                生成中的占位格 —— 排在**最前面**（新图会落在候选最前），
                这样产出落地时是「这一格被填上」，而不是「列表末尾多了一张」。
              */}
              {source.pending
                ? Array.from({ length: source.pending }).map((_, i) => (
                    <div key={`pending-${i}`} className="refup__pick-cell">
                      <span className="refup__pending" title="正在生成…">
                        <span className="spinner" />
                      </span>
                    </div>
                  ))
                : null}
              {source.candidates.map((u, i) => {
                const picked = urls.includes(u)
                return (
                  /* 每一格是一个 **cell**（不是裸按钮）—— 放大键要挂在按钮的
                     旁边，理由见上面 `zoom` 那段注释 */
                  <div key={u} className="refup__pick-cell">
                    <button
                      type="button"
                      className="refup__pick-item"
                      data-picked={picked}
                      aria-pressed={picked}
                      title={picked ? '已用作参考图 · 点击取消' : '用作参考图'}
                      onClick={() => toggleCandidate(u)}
                    >
                      <SmartImage src={u} alt="" renderFallback={renderFallback} />
                      {picked ? (
                        <span className="refup__pick-tick">
                          <Icon name="check" size={11} />
                        </span>
                      ) : null}
                    </button>
                    {/* 放大键**无条件渲染**（早先是「图加载成功才给」）。
                        那条判据要求本组件自己维护加载状态，而加载已经交给
                        `SmartImage` 了 —— 再存一份就是同一个事实两个源头。
                        `verify-digital-human.py` 正好断言「放大键数 == 格数」，
                        无条件渲染让这条断言不再依赖「图有没有加载成功」。 */}
                    <button
                      type="button"
                      className="refup__pick-zoom"
                      data-zoom={u}
                      title="放大看这张"
                      aria-label="放大看这张"
                      onClick={() => setZoom({ list: 'candidates', at: i })}
                    >
                      <Icon name="expand" size={13} />
                    </button>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="refup__pick-empty">
              <span>
                {source.emptyText ??
                  (source.withUpload
                    ? `还没有「${source.name}」的产出 —— 去那一档生成一张，这里就会出现；也可以直接上传一张。`
                    : `本页还没跑过「${source.name}」—— 它生出来的图就是这一档的参考图，所以这一档不提供上传。`)}
              </span>
              {source.onGo ? (
                <Button variant="outline" size="sm" icon="bolt" onClick={source.onGo}>
                  {source.goLabel ?? `去生成${source.name}`}
                </Button>
              ) : null}
            </div>
          )}
        </div>
      ) : null}

      {/*
        放大预览 —— 复用结果网格那套 `ImageLightbox`（← → 切换 / Esc 关闭 /
        点背景关闭 / 打开时锁背景滚动 / **视频自动换成带 controls 的播放器**），
        **不另写一个遮罩**。

        已选缩略图与候选条**共用这一个实例**：它们只是同一次「看大图」的两份来源，
        各渲染一个遮罩就会有两套开/关状态。

        ⚠️ **必须挂在本组件最外层，不许塞进上面那个 `source ? … : null` 分支里**
        （2026-10-08 踩到，靠真机点一次才发现）：
        这个遮罩早先是「候选条专属」，所以写在那个分支内；而**已选缩略图**
        （参考视频 / 产品参考图全是这一类）**根本不传 `source`** ——
        点缩略图能改 state，遮罩却永远不渲染，表现就是「点了没反应」。
        要命的是**当时所有 DOM 断言全绿**：探针读的是 `.refup__item`（缩略图在），
        没有任何一条读过 `.lightbox`。给已选缩略图加预览的那天，它必须跟着搬出来。

        底部挂「用这张」：看清之后再决定用不用，本来就是同一个动作的两半；
        逼用户先关遮罩、再回候选条里把那一格找出来，是白加一步。
        已选那几张没有「用这张」可点（它们本来就已经被选上了），所以操作区只给候选条。
      */}
      {zoom ? (
        <ImageLightbox
          title={
            zoom.list === 'picked' ? `${title}预览` : (source?.title ?? `${source?.name}的产出`)
          }
          items={zoomItems}
          index={Math.min(zoom.at, zoomItems.length - 1)}
          onIndex={(i) => setZoom((z) => (z ? { ...z, at: i } : z))}
          onClose={() => setZoom(null)}
          renderActions={
            zoom.list === 'candidates'
              ? (_item, i) => {
                  const u = source?.candidates[i]
                  if (!u) return null
                  const picked = urls.includes(u)
                  return (
                    <Button
                      variant={picked ? 'outline' : 'primary'}
                      size="sm"
                      icon={picked ? 'check' : 'plus'}
                      onClick={() => toggleCandidate(u)}
                    >
                      {picked ? '已用作参考图' : '用这张'}
                    </Button>
                  )
                }
              : undefined
          }
        />
      ) : null}

      {/*
        上传区 —— 什么情况下渲染，判据只有一条：**候选条没有接管这条路**。
          · 没传 `source`：常规通道，当然渲染（`isCell` 除外，它长在工具条里）；
          · 传了 `source` 且 `withUpload`：两条路并存（数字人的形象图）；
          · 传了 `source` 且没 `withUpload`：候选条独占（场景图那类「参考图只能来自那里」的档）。
        第一版把后两种混成了一件事（候选条挂了、上传框照旧在上面），
        用户一眼看到的仍然是一个「上传商品组件」。
      */}
      {source && !source.withUpload ? null : isCell ? null : (
        <label
          className={`refup__drop${over ? ' is-over' : ''}${full ? ' is-full' : ''}`}
          {...dropProps}
        >
          {fileInput}
          <span className="refup__drop-icon">
            {busy ? <span className="spinner" /> : <Icon name="upload" size={18} />}
          </span>
          {/*
            ⚠️ 满了之后的文案要**按格数**说，不能一句通用话糊过去。

            只有一格时（数字人的形象图就是这样，`max={1}`）：
              · 「已满 1 个，移除后可继续添加」还勉强，但「继续添加」本来就不该发生；
              · 「拖拽缩略图可调整顺序，第一张作为主图」—— 一格**没有顺序**可言，
                而「主图」是别的页面的概念；这一页的徽章写的是「出镜」，
                页面自己也传了 `dropHint`（「正脸 · 单人 · 光线均匀」）——
                那恰恰是用户此刻最该看见的一句，却被通用文案顶掉了。

            判据：**能派生的别写死**。格数已经在这里，文案就该跟着它走。
          */}
          <span className="refup__drop-main">
            {full ? (max > 1 ? `已满 ${max} 个，移除后可继续添加` : '已满 1 张，移除后可换一张') : dropLabel ?? `拖拽或点击上传${noun}`}
          </span>
          <span className="refup__drop-sub">
            {full
              ? max > 1
                ? '拖拽缩略图可调整顺序，第一张作为主图'
                : dropHint ?? '移除后可换一张'
              : dropHint ?? `支持多选 · 可拖拽排序 · 第一张作为主图`}
          </span>
        </label>
      )}

      {urls.length ? (
        <div
          className="refup__grid"
          onDragOver={(e) => e.preventDefault()}
          /*
           * card 形态：网格里的松手是「拖拽排序」，不许冒泡到上面那个上传区
           * （否则一次排序会被当成一次文件投放）。
           * cell 形态反过来了：**落点就是整个格子**，冒泡必须放行 ——
           * 这里若也 stopPropagation，往缩略图上拖文件会变成完全没反应。
           */
          onDrop={isCell ? undefined : (e) => e.stopPropagation()}
        >
          {urls.map((u, i) => {
            return (
              <div
                key={`${u}-${i}`}
                className="refup__item"
                data-first={i === 0}
                data-over={overIdx === i}
                draggable
                /*
                 * 点缩略图 = 看大图（视频就是播放）。
                 *
                 * 为什么必须有：这一格是 76px，而它是要喂给上游的那一张 ——
                 * 是不是传错了文件、视频是不是那条，在这个尺寸上根本看不出来。
                 * 用户口径：「为什么上传后是这样的，应该可以预览啊」。
                 *
                 * 与「拖拽排序」不冲突：一次拖拽不会触发 click。
                 * 也没有「选中」这个语义要避让（点它只是看），所以整格可点，
                 * 不必像候选格那样单挂一颗放大键 —— 那种做法是给「点图 = 选中」用的。
                 */
                role="button"
                tabIndex={0}
                data-zoom={u}
                title="点击预览"
                onClick={() => setZoom({ list: 'picked', at: i })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setZoom({ list: 'picked', at: i })
                  }
                }}
                onDragStart={(e) => {
                  dragFrom.current = i
                  e.dataTransfer.effectAllowed = 'move'
                  // Firefox 不设 data 就不触发 drag
                  e.dataTransfer.setData('text/plain', String(i))
                }}
                onDragEnd={() => {
                  dragFrom.current = null
                  setOverIdx(null)
                }}
                onDragOver={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (overIdx !== i) setOverIdx(i)
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  move(dragFrom.current ?? -1, i)
                  dragFrom.current = null
                  setOverIdx(null)
                }}
              >
                <SmartImage src={u} alt={`${title} ${i + 1}`} renderFallback={renderFallback} />
                <span className="refup__idx">{i + 1}</span>
                {i === 0 && firstBadge ? <span className="refup__main">{firstBadge}</span> : null}
                <button
                  type="button"
                  className="refup__x"
                  aria-label={`移除第 ${i + 1} 张`}
                  onClick={(e) => {
                    e.stopPropagation()
                    removeAt(i)
                  }}
                >
                  <Icon name="x" size={11} />
                </button>
              </div>
            )
          })}
          {/* cell 形态的计数挂在这一行末尾（见 `countInBar` 的说明） */}
          {countInBar ? null : countNode}
        </div>
      ) : null}

      <div className="refup__bar">
        {/*
          cell 形态的「加图」按钮长在工具条里（不放上面）：
          格子里排下来应该是「缩略图 → 操作」，镜像过来会出现「先出现加号、
          下面才是已选的那几张」，看着像没选上。
        */}
        {isCell ? (
          <label
            className={`refup__add${full ? ' is-full' : ''}${busy ? ' is-busy' : ''}`}
            data-busy={busy ? '1' : '0'}
            aria-busy={busy}
            title={full ? `已满 ${max} ${noun}，先移除几张` : `上传${noun}`}
          >
            {fileInput}
            {busy ? <span className="spinner" /> : <Icon name="plus" size={12} />}
            <span>{full ? '已满' : busy ? '上传中' : '上传'}</span>
          </label>
        ) : null}
        {/* 候选条**独占**时没有上传通道，素材库按钮一并收掉：
            它给的是「随便挑一张」，会直接绕开这一档「参考图只能来自那个档」的约束，
            两个入口摆在一起，用户会以为两条路等价。
            （`withUpload` 时相反 —— 素材库本来就是这个控件的常规能力，收掉才是漏入口。） */}
        {source && !source.withUpload ? null : isCell ? (
          <Button
            variant="outline"
            size="sm"
            icon="archive"
            aria-label="从素材库选择"
            title="从素材库选择"
            onClick={() => void fromLibrary()}
          />
        ) : (
          <Button variant="outline" size="sm" icon="archive" onClick={() => void fromLibrary()}>
            从素材库选择
          </Button>
        )}
        {countInBar ? countNode : null}
        {/*
          清空。**内建在组件里，不给页面开关** —— 2026-09-29 用户反馈的正是
          「封装为一个、其他页面引用，现在少了清空这块」：这套素材卡在 7 个页面
          出现约 10 次，清空若靠各页自己接，就必然出现「这个页面能清、那个不能」，
          用户以为是 bug、其实是抄漏 —— 而「有没有清空」截图上一眼看不出来。
          所以：能力跟着控件走，不跟着页面走。

          走 ClearButton 而不是自己拼按钮：二次确认的复位计时 / 危险态配色 /
          无障碍文案三件事只有一份实现（见 ui.tsx）。
        */}
        {urls.length ? (
          <ClearButton
            count={urls.length}
            unit={kind === 'image' ? '张' : '个'}
            title={isCell ? '移除本行全部参考图' : '移除本卡已选的全部素材'}
            iconOnly={isCell}
            onClear={() => onChange([])}
          />
        ) : null}
        {footnote ? <span className="refup__note">{footnote}</span> : null}
      </div>

      {!urls.length && emptyHint ? <div className="refup__empty">{emptyHint}</div> : null}
    </div>
  )

  // cell：既不要卡片壳（它就在表格格子里），也不要 `Field` 的标签行
  //（表头已经写着「参考图」了，再来一个重复的标签）
  if (wrap === 'cell') return control

  if (wrap === 'none') {
    return (
      <Field label={label} required={required} hint={fieldHint}>
        {control}
      </Field>
    )
  }

  return (
    <Card>
      <CardHead
        title={title}
        hint={hint}
        icon={icon}
        right={
          urls.length ? (
            <Badge tone={urls.length >= max ? 'warning' : 'neutral'}>
              {urls.length}/{max}
            </Badge>
          ) : null
        }
      />
      <CardBody>{control}</CardBody>
    </Card>
  )
}
